import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Skeleton, Snackbar, Alert } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";

import { useWorkspaceConfig } from "../../context/WorkspaceConfigContext";
import { useSetupOnboardingStatus } from "../../hooks/useSetupOnboardingStatus";
import { useEffectivePermissions, toSet, canAny } from "../../hooks/useEffectivePermissions";
import useChatState from "./hooks/useChatState";
import useMessageHandler from "./hooks/useMessageHandler";
import { useRequests } from "../../hooks/useRequests";
import ChatLayout from "./ChatLayout";
import EmptyState from "./EmptyState";
import NewRequestModal from "./NewRequestModal";
import { getConversationById } from "../../api/client";
import { ensureNamespaceLoaded } from "../../i18n/lazyNamespaces";

/* Skeleton que replica o layout do Chat (lista + área central) */
function ChatInitialSkeleton() {
  return (
    <Box
      sx={(t) => ({
        minHeight: "calc(100vh - 120px)",
        display: "flex",
        gap: 1,
        p: 1,
        backgroundImage: `linear-gradient(${alpha(t.palette.primary.main, 0.04)}, transparent)`,
      })}
    >
      {/* Painel esquerdo: lista de conversas */}
      <Box
        sx={{
          width: { xs: "100%", md: "25%" },
          minWidth: 280,
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.paper",
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        <Box sx={{ p: 1, display: "flex", gap: 0.5, alignItems: "center", height: 62 }}>
          <Skeleton variant="rounded" width={40} height={40} />
          <Skeleton variant="rounded" width={40} height={40} />
          <Skeleton variant="rounded" width="60%" height={40} sx={{ flex: 1 }} />
        </Box>
        <Box sx={{ px: 1, pb: 1 }}>
          <Skeleton variant="text" width="90%" height={56} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="85%" height={56} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="92%" height={56} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="78%" height={56} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="88%" height={56} />
        </Box>
      </Box>
      {/* Área central: cabeçalho + mensagens */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.paper",
          borderRadius: 1,
          overflow: "hidden",
          minWidth: 0,
        }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
          <Skeleton variant="circular" width={40} height={40} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="40%" />
          <Skeleton variant="text" width="25%" sx={{ mt: 0.5 }} />
        </Box>
        <Box sx={{ flex: 1, p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
          <Skeleton variant="rounded" width="70%" height={48} />
          <Skeleton variant="rounded" width="50%" height={48} sx={{ alignSelf: "flex-end" }} />
          <Skeleton variant="rounded" width="65%" height={48} />
          <Skeleton variant="rounded" width="55%" height={48} sx={{ alignSelf: "flex-end" }} />
        </Box>
      </Box>
    </Box>
  );
}

const Chat = () => {
  const { t } = useTranslation("chat");
  const { t: tOnb } = useTranslation("onboarding");
  const { companyKey, isLoading: workspaceLoading } = useWorkspaceConfig();
  const { data: permData, isLoading: permsLoading } = useEffectivePermissions();
  const scopesSet = toSet(permData?.scopes);
  const canConfigureWorkspace = canAny(scopesSet, [
    ["companies", "view"],
    ["channels", "view"],
    ["users", "view"],
    ["types", "view"],
    ["automation_rules", "view"],
  ]);
  const {
    data: serverOnboarding,
    isLoading: serverOnboardingLoading,
    isError: serverOnboardingError,
  } = useSetupOnboardingStatus(
    companyKey,
    Boolean(companyKey && !workspaceLoading && !permsLoading)
  );

  useEffect(() => {
    void ensureNamespaceLoaded("onboarding");
  }, []);

  const {
    isMobile,
    userData,
    selectedConversationId,
    selectedConversation,
    showConversationList,
    showMessageHistory,
    showConversationDetails,
    isLoadingDetails,
    initialLoading,
    isLoadingConversation,
    messagesSyncError,
    reloadConversationMessages,
    messages,
    conversations,
    totalInDatabase,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    refetch,
    searchTerm,
    setSearchTerm,
    filters,
    setFilters,
    setShowConversationList,
    setShowMessageHistory,
    setShowConversationDetails,
    setSelectedConversation,
    setMessages,
    handleSelectConversation,
    handleEndConversation,
    containerStyle,
    sendMessageMutation,
    isError,
    createConversation,
    isCreatingConversation,
    aiInsights,
    isLoadingInsights,
    aiInsightsError,
    loadAiInsights,
    hasMoreMessages,
    isLoadingMoreMessages,
    loadOlderMessages,
    refetchConversationDetails,
    handleDeleteMessage,
    deletePersonalConversation,
  } = useChatState();

  const { updateMutation } = useRequests();

  const { handleSendMessage } = useMessageHandler({
    selectedConversation,
    userData,
    setMessages,
    sendMessageMutation,
    updateMutation,
    onUpdateConversation: (updatedFields) =>
      setSelectedConversation((prev) => (prev ? { ...prev, ...updatedFields } : prev)),
  });

  const onDeletePersonalConversation = async () => {
    try {
      await deletePersonalConversation();
      setSnackbar({
        open: true,
        message: t("pane.deletePersonalSuccess"),
        severity: "success",
      });
    } catch (err) {
      const raw =
        (typeof err?.message === "string" && err.message) ||
        (typeof err === "string" && err) ||
        (Array.isArray(err?.message) && err.message[0]) ||
        null;
      setSnackbar({
        open: true,
        message: raw || t("pane.deletePersonalFailed"),
        severity: "error",
      });
      throw err;
    }
  };

  const onDeleteMessage = async (messageId) => {
    try {
      await handleDeleteMessage(messageId);
      setSnackbar({ open: true, message: t("page.messageDeleted"), severity: "success" });
    } catch (err) {
      const raw =
        (typeof err?.message === "string" && err.message) ||
        (typeof err === "string" && err) ||
        (Array.isArray(err?.message) && err.message[0]) ||
        null;
      setSnackbar({
        open: true,
        message: raw || t("page.messageDeleteFailed"),
        severity: "error",
      });
      throw err;
    }
  };

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "error" });
  const [newRequestModalOpen, setNewRequestModalOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Mostra feedback visível quando falha o carregamento/sync do histórico.
  const lastMessagesErrKeyRef = React.useRef("");
  useEffect(() => {
    if (!messagesSyncError) return;
    const key = `${messagesSyncError.code || ""}::${messagesSyncError.message || ""}`;
    if (key && key === lastMessagesErrKeyRef.current) return;
    lastMessagesErrKeyRef.current = key;
    setSnackbar({
      open: true,
      message: messagesSyncError.message || "Não foi possível carregar o histórico da conversa.",
      severity: messagesSyncError.code === "errors.clients.duplicate" ? "warning" : "error",
    });
  }, [messagesSyncError]);

  // Abre a conversa quando navegado após aceitar CallNotification (ex.: simulação em Configurações > Canais)
  useEffect(() => {
    const openId = location.state?.openConversationId;
    if (!openId || initialLoading) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getConversationById(openId);
        const dto = res?.data || res;
        if (cancelled || !dto) return;
        const client = dto.client || {};
        const conversation = {
          id: dto.id,
          name: client.name ?? dto.name ?? "",
          phoneNumber: client.phone ?? null,
          lastMessage: dto.lastMessage || "",
          timestamp: dto.timestamp,
          channel: dto.channel,
          status: dto.status,
          assignedTo: dto.userName ?? dto.assignedTo ?? null,
          unread: dto.unread || 0,
          statusLabel: "Em Atendimento",
        };
        handleSelectConversation(conversation);
        setShowConversationList(true);
        setShowMessageHistory(true);
        setShowConversationDetails(true);
        navigate(location.pathname, { replace: true, state: {} });
      } catch (err) {
        if (!cancelled) console.error("[Chat] Erro ao abrir conversa openConversationId:", err);
      }
    })();
    return () => { cancelled = true; };
  }, [
    location.state?.openConversationId,
    initialLoading,
    navigate,
    location.pathname,
    handleSelectConversation,
    setShowConversationList,
    setShowMessageHistory,
    setShowConversationDetails,
    t,
  ]);

  if (initialLoading) return <ChatInitialSkeleton />;

  const showWorkspaceSetupPendingBanner =
    !serverOnboardingLoading &&
    !serverOnboardingError &&
    serverOnboarding?.needsSetup === true &&
    !canConfigureWorkspace;

  const hasConversations = Array.isArray(conversations) && conversations.length > 0;
  const dbTotal = typeof totalInDatabase === "number" ? totalInDatabase : null;
  // EmptyState só quando não há nenhuma conversa no banco e não estamos abrindo uma por URL/id.
  const showEmpty =
    !initialLoading &&
    !hasConversations &&
    dbTotal === 0 &&
    !selectedConversation &&
    selectedConversationId == null;
  const listIsFetching = Boolean(isFetching && hasConversations);
  const listIsFetchingNextPage = Boolean(isFetchingNextPage && hasConversations);

  const getProjectId = () => {
    try {
      const raw = sessionStorage.getItem("selectedCompanies");
      const arr = JSON.parse(raw || "[]");
      if (Array.isArray(arr) && arr.length > 0) return Number(arr[0]);
    } catch {}
    return null;
  };

  async function handleNewConversation(payloadOverrides = {}) {
    try {
      const hasClient =
        (payloadOverrides.client && Object.keys(payloadOverrides.client).length > 0) ||
        payloadOverrides.clientId != null;
      if (!hasClient) {
        setSnackbar({
          open: true,
          message: t("page.selectClientForRequest"),
          severity: "warning",
        });
        return;
      }

      const projectId = getProjectId();
      const payload = {
        projectId: projectId || undefined,
        client: payloadOverrides.client ?? {},
        clientId: payloadOverrides.clientId,
        typeId: payloadOverrides.typeId ?? undefined,
        subtype1: payloadOverrides.subtype1,
        subtype2: payloadOverrides.subtype2,
        subtype3: payloadOverrides.subtype3,
        channelId: payloadOverrides.channelId,
        assignedUserId: payloadOverrides.assignedUserId,
        typeKind: payloadOverrides.typeKind ?? "1",
        initialMessage: payloadOverrides.initialMessage ?? "",
        source: "chat",
        ...payloadOverrides,
      };

      const created = await createConversation(payload);

      handleSelectConversation({
        id: created.id,
        name: created.name,
        lastMessage: created.lastMessage || "",
        timestamp: created.timestamp,
        channel: created.channel,
        status: created.status,
        assignedTo: created.assignedTo,
        unread: created.unread || 0,
        statusLabel: t("page.statusInProgress"),
      });
      setSnackbar({ open: true, message: t("page.requestCreated"), severity: "success" });
      return created;
    } catch (err) {
      console.error("Falha ao criar atendimento:", err);
      const msg = err?.response?.data?.message || err?.message || "Não foi possível criar o atendimento. Tente novamente.";
      setSnackbar({ open: true, message: msg, severity: "error" });
      throw err;
    }
  }

  if (showEmpty) {
    return (
      <>
        {showWorkspaceSetupPendingBanner ? (
          <Alert severity="info" sx={{ mb: 1 }}>
            {tOnb("setup.chatPendingAdmin")}
          </Alert>
        ) : null}
        <EmptyState
          userName={userData?.nome || userData?.name}
          onNewConversation={() => setNewRequestModalOpen(true)}
          isCreating={isCreatingConversation}
        />
        <NewRequestModal
          open={newRequestModalOpen}
          onClose={() => setNewRequestModalOpen(false)}
          onCreate={handleNewConversation}
          isCreating={isCreatingConversation}
          currentUser={userData}
        />
        <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
          <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </>
    );
  }

  return (
    <>
      {showWorkspaceSetupPendingBanner ? (
        <Alert severity="info" sx={{ mb: 1 }}>
          {tOnb("setup.chatPendingAdmin")}
        </Alert>
      ) : null}
      <ChatLayout
        isMobile={isMobile}
        containerStyle={containerStyle}
        userData={userData}
        selectedConversationId={selectedConversationId}
        selectedConversation={selectedConversation}
        showConversationList={showConversationList}
        showMessageHistory={showMessageHistory}
        showConversationDetails={showConversationDetails}
        isLoadingDetails={isLoadingDetails}
        initialLoading={initialLoading}
        isLoadingConversation={isLoadingConversation}
        messagesSyncError={messagesSyncError}
        reloadConversationMessages={reloadConversationMessages}
        messages={messages}
        conversations={conversations}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={listIsFetchingNextPage}
        isFetching={listIsFetching}
        refetchConversations={refetch}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        filters={filters}
        setFilters={setFilters}
        onSelectConversation={handleSelectConversation}
        onSendMessage={handleSendMessage}
        onEndConversation={handleEndConversation}
        onUpdateConversation={(updatedFields) =>
          setSelectedConversation((prev) => ({ ...prev, ...updatedFields }))
        }
        onBack={() => {
          setShowConversationList(true);
          setShowMessageHistory(false);
          setShowConversationDetails(false);
        }}
        onShowDetails={() => {
          if (isMobile) {
            setShowConversationDetails((prev) => {
              const next = !prev;
              // No mobile: quando abre detalhes, esconde mensagens;
              // quando fecha detalhes, volta a mostrar o histórico.
              setShowMessageHistory(!next);
              return next;
            });
          } else {
            setShowConversationDetails((prev) => !prev);
          }
        }}
        onOpenPopup={() => {
          const width = window.screen.width;
          const height = window.screen.height;
          window.open(
            `/popup-call/${selectedConversation?.id}`,
            t("page.popupWindowName"),
            `width=${width},height=${height},top=0,left=0,scrollbars=no,toolbar=no,location=no,status=no,menubar=no`
          );
        }}
        onCloseConversationList={() => setShowConversationList(false)}
        onNewConversation={() => setNewRequestModalOpen(true)}
        isCreatingConversation={isCreatingConversation}
        isError={isError}
        aiInsights={aiInsights}
        isLoadingInsights={isLoadingInsights}
        aiInsightsError={aiInsightsError}
        onLoadAiInsights={loadAiInsights}
        hasMoreMessages={hasMoreMessages}
        isLoadingMoreMessages={isLoadingMoreMessages}
        onLoadMoreMessages={loadOlderMessages}
        onRefetchConversationDetails={refetchConversationDetails}
        onDeleteMessage={onDeleteMessage}
        onDeletePersonalConversation={onDeletePersonalConversation}
      />
      <NewRequestModal
        open={newRequestModalOpen}
        onClose={() => setNewRequestModalOpen(false)}
        onCreate={handleNewConversation}
        isCreating={isCreatingConversation}
        currentUser={userData}
      />
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Chat;
